import { SlI18nModule } from '@screenleads/i18n';
import { slText } from '@screenleads/i18n';
import {CaptureLanguageService} from '../../../core/i18n/capture-language.service';
import {FormsModule} from '@angular/forms';
import {Subject,takeUntil,timer,take} from 'rxjs';
import { customerReturnUrl } from '../../../core/utils/return-url';
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { SlTextFieldComponent, SlNativeControlDirective, SlAlertComponent, SlAvatarComponent, SlIconComponent, SlCardComponent, SlButtonComponent, SlAuthLoginComponent, SlAuthLoginConfig, SlAuthSocialProvider, SlAuthSubmitPayload, SlCompanyColorsService } from 'sl-dev-components';
import { AuthService, PromotionService } from '../../../core/services';
import { FlowContextStore } from '../../../core/stores';
import { BrandingStateService } from '../../../core/services/branding-state.service';
import { Customer, Promotion } from '../../../core/models';


@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, SlTextFieldComponent, SlNativeControlDirective, SlAlertComponent, SlAuthLoginComponent, SlCardComponent, SlButtonComponent, SlAvatarComponent, SlIconComponent,SlI18nModule],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {
 readonly i18n=inject(CaptureLanguageService);
  @Input() presentation: 'page' | 'popup' = 'page';
  @Input() context: {returnUrl:string; queryParams:Record<string,any>} | null = null;
  @Output() authenticated = new EventEmitter<void>();
  @Output() exitPopup = new EventEmitter<void>();
  private readonly destroyed = new Subject<void>();
  private readonly linkCancelled = new Subject<void>();
  ngOnDestroy(): void {
    this.authService.cancelPendingAccountLink();
    this.destroyed.next(); this.destroyed.complete();
  }

  accountLink = signal<{ email: string; provider: string } | null>(null);
  accountLinkConfirmed = signal(false);
  verifiedAccount = signal<Customer | null>(null);
  verifiedAvatar = signal<string | null>(null);
  readonly accountName = computed(() => {
    const account = this.verifiedAccount();
    return account ? [account.firstName, account.lastName].filter(Boolean).join(' ') || slText("Tu cuenta") : slText("Tu cuenta de ScreenLeads");
  });

  addLoginMethod(): void {
    this.ssoLoading.set('link');
    this.error.set(null);
    this.authService.confirmPendingAccountLink().pipe(takeUntil(this.destroyed)).subscribe({
      next: () => this.navigateToReturnUrl(),
      error: err => { this.cancelAccountLink(); this.error.set(err.message); this.ssoLoading.set(null); }
    });
  }

  verificationCode='';
  resendSeconds=signal(0);
  confirmAccountLink(): void {
    if(this.ssoLoading() || this.resendSeconds()>0)return;
    this.ssoLoading.set('email-code');this.error.set(null);
    this.authService.requestAccountLinkCode().pipe(takeUntil(this.destroyed),takeUntil(this.linkCancelled)).subscribe({
      next: response=>{
        this.accountLinkConfirmed.set(true);this.verificationCode='';this.ssoLoading.set(null);
        timer(0,1000).pipe(take(response.resendAfterSeconds+1),takeUntil(this.destroyed),takeUntil(this.linkCancelled)).subscribe(elapsed=>this.resendSeconds.set(Math.max(0,response.resendAfterSeconds-elapsed)));
      },
      error: err=>{this.error.set(err?.error?.detail || err?.error?.message || err.message || 'No se pudo enviar el código.');this.ssoLoading.set(null);}
    });
  }
  verifyAccountLink(): void {
    if(this.ssoLoading() || !/^[0-9]{6}$/.test(this.verificationCode))return;
    this.ssoLoading.set('verify-code');this.error.set(null);
    this.authService.verifyAccountLinkCode(this.verificationCode).pipe(takeUntil(this.destroyed),takeUntil(this.linkCancelled)).subscribe({
      next:()=>this.navigateToReturnUrl(),
      error:err=>{this.error.set(err?.error?.detail || err?.error?.message || err.message || 'No se pudo verificar el código.');this.ssoLoading.set(null);}
    });
  }

  cancelAccountLink(): void {
    this.linkCancelled.next();this.ssoLoading.set(null);
    this.authService.cancelPendingAccountLink();
    this.accountLink.set(null);
    this.accountLinkConfirmed.set(false);
    this.verificationCode='';this.resendSeconds.set(0);
    this.verifiedAccount.set(null);
    this.verifiedAvatar.set(null);
    this.error.set(null);
  }

  loading = signal(false);
  ssoLoading = signal<string | null>(null);
  error = signal<string | null>(null);
  promotion = signal<Promotion | null>(null);
  promotionLoading = signal(false);
  returnUrl: string = '/promotions';
  promotionId?: number;
  deviceId?: number;
  companyId?: number;
  queryParams: any = {};

  readonly loginConfig = computed<SlAuthLoginConfig>(() => {
    const activeBranding = this.companyColors.activeBranding();
    const promotionCompany = this.promotion()?.company as Record<string, string | undefined> | undefined;
    const promotionLogoUrl = promotionCompany
      ? promotionCompany['logoWideUrl'] || promotionCompany['logoOnDarkUrl'] || promotionCompany['logoUrl']
      : null;
    const resolvedLogo = promotionLogoUrl
      ? this.resolveMediaUrl(promotionLogoUrl)
      : (activeBranding.logoWideUrl || null);
    const resolvedName = promotionCompany?.['name'] || activeBranding.companyName || 'ScreenLeads';

    return {
      entity: 'consumer',
      initialMode: 'login',
      features: {
        enableRegister: !this.accountLink(),
        enableForgot: !this.accountLink(),
        enableRemember: false,
        identifierType: 'email'
      },
      social: {
        providers: this.accountLink() ? ['google', 'apple', 'microsoft'] : ['google', 'facebook', 'apple', 'microsoft']
      },
      branding: {
        companyName: resolvedName,
        logoWideUrl: resolvedLogo ?? undefined
      },
      copy: {
        login: {
          title: this.accountLink() ? slText("Añadir {p0}", {p0:(this.accountLink()!.provider)}) : this.i18n.copy().welcome,
          subtitle: this.accountLink() ? slText("Confirma tu identidad con el método que ya utilizas. Lo conectaremos automáticamente.") : this.presentation === 'popup' ? this.i18n.copy().authContinue : this.i18n.copy().loginIntro,
          identifierLabel: slText("Email"),
          identifierPlaceholder: slText("tu@email.com"),
          submitLabel: this.i18n.copy().login
        }
      }
    };
  });

  constructor(
    private authService: AuthService,
    private promotionService: PromotionService,
    private router: Router,
    private route: ActivatedRoute,
    private flowContextStore: FlowContextStore
  ) {}

  readonly brandingState = inject(BrandingStateService);
  private readonly companyColors = inject(SlCompanyColorsService);

  ngOnInit() {
    const allParams = this.presentation === 'popup' && this.context ? {...this.context.queryParams, returnUrl:this.context.returnUrl} : this.route.snapshot.queryParams;
    const storeState = this.flowContextStore.state();
    this.returnUrl = customerReturnUrl(allParams['returnUrl'] || storeState.returnUrl);
    this.promotionId = allParams['promotionId'] ? +allParams['promotionId'] : undefined;
    this.deviceId = allParams['deviceId'] ? +allParams['deviceId'] : (storeState.deviceId || undefined);
    this.companyId = allParams['companyId'] ? +allParams['companyId'] : (storeState.companyId || undefined);

    this.queryParams = { ...allParams };
    delete this.queryParams['returnUrl'];
    if (this.promotionId) this.queryParams['promotionId'] = this.promotionId;
    if (this.deviceId) this.queryParams['deviceId'] = this.deviceId;
    if (this.companyId) this.queryParams['companyId'] = this.companyId;
    if (this.presentation === 'page' && !this.queryParams['autoGenerateCoupon'] && storeState.autoGenerateCoupon) {
      this.queryParams['autoGenerateCoupon'] = 'true';
    }
    if (this.presentation === 'page' && !this.queryParams['autoEnterSweepstakes'] && storeState.autoEnterSweepstakes) {
      this.queryParams['autoEnterSweepstakes'] = 'true';
    }

    this.flowContextStore.setContext({
      promotionId: this.promotionId || null,
      companyId: this.companyId || null,
      deviceId: this.deviceId || null,
      returnUrl: this.returnUrl,
      autoGenerateCoupon: this.queryParams['autoGenerateCoupon'] === 'true' || (this.presentation === 'page' && storeState.autoGenerateCoupon),
      autoEnterSweepstakes: this.queryParams['autoEnterSweepstakes'] === 'true' || (this.presentation === 'page' && storeState.autoEnterSweepstakes),
      originRoute: this.router.url
    });

    if (this.promotionId) {
      this.loadPromotionBranding();
    }
  }



  private navigateToReturnUrl() {
    this.flowContextStore.setContext({
      promotionId: this.promotionId || null,
      companyId: this.companyId || null,
      deviceId: this.deviceId || null,
      returnUrl: this.returnUrl,
      autoGenerateCoupon: this.queryParams['autoGenerateCoupon'] === 'true',
      autoEnterSweepstakes: this.queryParams['autoEnterSweepstakes'] === 'true'
    });
    const targetUrlTree = this.router.parseUrl(this.returnUrl || '/promotions');
    targetUrlTree.queryParams = { ...targetUrlTree.queryParams, ...this.queryParams };
    this.authenticated.emit();
    this.router.navigateByUrl(targetUrlTree);
  }

  private loadPromotionBranding() {
    this.promotionLoading.set(true);
    this.promotionService.getPublicPromotion(this.promotionId!).pipe(takeUntil(this.destroyed)).subscribe({
      next: (promo) => {
        this.promotion.set(promo);
        this.promotionLoading.set(false);
      },
      error: () => {
        this.promotionLoading.set(false);
      }
    });
  }

  getBrandLogoUrl(): string | null {
    const company = (this.promotion()?.company || {}) as Record<string, string | undefined>;
    const logoUrl = company['logoWideUrl'] || company['logoOnDarkUrl'] || company['logoUrl'];
    if (logoUrl) {
      return this.resolveMediaUrl(logoUrl);
    }
    // Fall back to the logo already set by AppComponent from DB company data
    const activeLogo = this.companyColors.activeBranding().logoWideUrl;
    return activeLogo || '/logo-hor.svg';
  }

  private resolveMediaUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return url;
    return `/assets/${url}`;
  }

  onModeChanged(mode: string): void {
    if (mode === 'register') {
      this.exitPopup.emit();
      this.router.navigateByUrl(this.getRegisterUrl());
    }
  }

  onSubmitRequested(event: SlAuthSubmitPayload) {
    if (event.mode !== 'login') {
      this.error.set(slText("La recuperación de esta cuenta necesita asistencia. Contacta con la empresa."));
      return;
    }
    const email = String(event.values['identifier'] || '').trim();
    const password = String(event.values['password'] || '');
    if (!email || !password) {
      this.error.set(slText("Completa email y contraseña"));
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const companyId = this.companyId || this.flowContextStore.companyId() || null;
    if (!companyId) {
      this.error.set(slText("No se pudo resolver la empresa para iniciar sesion"));
      this.loading.set(false);
      return;
    }

    this.authService.login({ email, password, companyId }).pipe(takeUntil(this.destroyed)).subscribe({

      next: () => {
        this.navigateToReturnUrl();
      },
      error: (err) => {
        this.error.set(err.message || 'Credenciales incorrectas');
        this.loading.set(false);
      }
    });
  }

  onSocialRequested(provider: SlAuthSocialProvider) {
    const providerKey = String(provider);
    this.ssoLoading.set(providerKey);
    this.error.set(null);

    const handlers: Record<SlAuthSocialProvider, () => any> = {
      google: () => this.authService.loginWithGoogle(),
      facebook: () => this.authService.loginWithFacebook(),
      apple: () => this.authService.loginWithApple(),
      microsoft: () => this.authService.loginWithMicrosoft()
    };

    handlers[provider]().pipe(takeUntil(this.destroyed)).subscribe({
      next: (response: {customer: Customer; accessToken:string}) => {
        if (this.accountLink() && this.authService.getPendingAccountLink()) {
          this.verifiedAccount.set(response.customer);
          this.verifiedAvatar.set(this.authService.getVerifiedAccountPhoto());
          this.ssoLoading.set(null);
        } else this.navigateToReturnUrl();
      },
      error: (err: any) => {
        const pending = this.authService.getPendingAccountLink();
        this.verifiedAccount.set(null);
        this.verifiedAvatar.set(null);
        if (pending) {
          this.accountLink.set(pending);
          this.error.set(null);
        } else {
          this.accountLink.set(null);
          this.accountLinkConfirmed.set(false);
          this.error.set(err?.message || `Error al iniciar sesión con ${providerKey}`);
        }
        this.ssoLoading.set(null);
      }
    });
  }

  getRegisterUrl(): string {
    let url = `/register?returnUrl=${encodeURIComponent(this.returnUrl)}`;
    for (const [key, value] of Object.entries(this.queryParams)) {
      url += `&${key}=${encodeURIComponent(value as string)}`;
    }
    return url;
  }

}
